import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { updateDataLayer } from "@utils/updateDataLayer";
import { getPaywallCountdown } from "src/appConfig";
import {
  FAQSection,
  Guarantee,
  Partners,
  PaywallChart,
  PersonalPlan,
  Plan,
  ResultsSlider,
  Reviews,
  Timer,
  Transform,
} from "../../shared/components";

import Normal from "./img/normal.png";
import Overweight from "./img/overweight.png";
import Obese from "./img/obese.png";
import Underweight from "./img/underweight.png";
import Lean from "./img/lean.png";
import Toned from "./img/toned.png";
import Average from "./img/average.png";
import SlightlyLighter from "./img/few_pounds_less.png";
import { ReliefSlider } from "./components/lastingRelief";
import { UpsaleModal } from "../../shared/components/upsaleModal";
import { AdditionalDiscount } from "../../shared/components/additionalDiscount";
import { Benefits } from "./components/benefits";
import { CalorieCounter } from "../../shared/components/calorieCounter";
import { ProfileCard } from "./components/profileCard";
import useLocationEvent from "@hooks/useLocationEvent";
import { DiscountProvider } from "@hooks/useDiscount";

const BMI_TYPE_IMAGES = (desiredBodyType) => {
  const After = {
    Lean,
    Toned,
    Average,
    SlightlyLighter,
  };
  return {
    NORMAL: Normal,
    OVERWEIGHT: Overweight,
    OBESE: Obese,
    UNDERWEIGHT: Underweight,
    After: After[desiredBodyType.replace(/\\?"/g, "")],
  };
};

export const SomaticPaywall = () => {
  useLocationEvent();
  const storedCountdown = getPaywallCountdown();
  const parsedCountdown = storedCountdown
    ? parseInt(storedCountdown, 10)
    : null;

  const skipTimer = parseInt(parsedCountdown) === -1;
  
  const [upsaleModalVisible, setUpsaleModalVisible] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const profileCardRef = useRef(null);
  const planRef = useRef(null);

  useEffect(() => {
    if (skipTimer) return;

    const handleScroll = () => {
      if (profileCardRef.current && !showTimer) {
        const rect = profileCardRef.current.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

        if (isVisible) {
          setShowTimer(true);
          window.removeEventListener("scroll", handleScroll);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollToPlan = () => {
    planRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const faqSectionRef = useRef(null);

  const name = localStorage.getItem("name");

  const additionalDiscount =
    localStorage.getItem("additionalDiscount") === "true";

  const handleCheckoutClose = () => {
    setUpsaleModalVisible(true);
  };

  return (
    <DiscountProvider>
      <div className={styles.layout}>
        {showTimer && (
          <Timer
            onGetMyPlanClick={handleScrollToPlan}
            onTimeEnd={() => setShowTimer(false)}
            customTime={parsedCountdown * 1000}
          />
        )}
        {additionalDiscount && <AdditionalDiscount />}
        <Transform
          images={BMI_TYPE_IMAGES(
            localStorage.getItem("targetBody") || "Toned"
          )}
          title="Somatic exercise"
          header="After the program"
        />
        <PersonalPlan isSomatic />
        <CalorieCounter />
        <div ref={planRef}>
          <Plan
            id={1}
            title="AI-optimized plan"
            isMetrics
            onCloseCheckout={handleCheckoutClose}
            additionalDiscount={additionalDiscount}
                        showTimer={showTimer}
          />
        </div>
        <div ref={profileCardRef}>
          <ProfileCard />
        </div>
        <PaywallChart />
        <Plan
          id={2}
          title="Get visible results in 4 weeks with AI‑optimized plan!"
          name={name}
          onCloseCheckout={handleCheckoutClose}
          additionalDiscount={additionalDiscount}
                      showTimer={showTimer}
        />
        <Benefits />
        <Partners />
        <ReliefSlider />
        <ResultsSlider />
        <div ref={faqSectionRef}>
          <FAQSection />
        </div>
        <Reviews />
        <Plan
          id={3}
          title="Get visible results in 4 weeks with AI‑optimized plan!"
          isMetrics
          onCloseCheckout={handleCheckoutClose}
          additionalDiscount={additionalDiscount}
                      showTimer={showTimer}
        />
        <Guarantee />
        <footer className={styles.footer}>
          Straiton Limited | Alpha Tower, Floor 1, Office 11, <br /> Pavlou
          Nirvana- Aipeias, Limassol, 3021, Cyprus
        </footer>
      </div>
      {upsaleModalVisible && (
        <UpsaleModal
          onClose={() => setUpsaleModalVisible(false)}
          text="Somatic Exercise Plan."
        />
      )}
    </DiscountProvider>
  );
};
